import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from 'lib/utils';

export type AgentDetailsTabItem = {
  label: string;
  href: string;
};

type AgentDetailsTabBarProps = {
  tabs: AgentDetailsTabItem[];
  activeHref: string;
  onTabClick: (href: string) => void;
  disabled?: boolean;
  className?: string;
  tabsClassName?: string;
  showDivider?: boolean;
  /** Inline header tabs (desktop) vs full-width row with divider (mobile). */
  variant?: 'inline' | 'fullWidth';
};

export function AgentDetailsTabBar({
  tabs,
  activeHref,
  onTabClick,
  disabled = false,
  className,
  tabsClassName,
  showDivider = true,
  variant = 'fullWidth',
}: AgentDetailsTabBarProps) {
  const isInline = variant === 'inline';
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = scrollContainerRef.current;
    if (!el) return;

    const { scrollLeft, scrollWidth, clientWidth } = el;
    const isScrollable = scrollWidth > clientWidth + 1;
    setCanScrollLeft(isScrollable && scrollLeft > 1);
    setCanScrollRight(isScrollable && scrollLeft + clientWidth < scrollWidth - 1);
  }, []);

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;

    updateScrollState();

    const resizeObserver = new ResizeObserver(() => {
      updateScrollState();
    });

    resizeObserver.observe(el);
    window.addEventListener('resize', updateScrollState);

    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', updateScrollState);
    };
  }, [updateScrollState, tabs]);

  useEffect(() => {
    const el = scrollContainerRef.current;
    if (!el) return;

    const activeTabEl = el.querySelector<HTMLElement>('[aria-selected="true"]');
    if (activeTabEl) {
      activeTabEl.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
        inline: 'nearest',
      });
    }
  }, [activeHref]);

  const handleScroll = (direction: 'left' | 'right') => {
    const el = scrollContainerRef.current;
    if (!el) return;

    const scrollAmount = Math.max(el.clientWidth * 0.6, 160);
    el.scrollBy({
      left: direction === 'left' ? -scrollAmount : scrollAmount,
      behavior: 'smooth',
    });
  };

  return (
    <div
      className={cn(
        'flex min-w-0 flex-col',
        isInline ? 'w-fit max-w-full items-stretch' : 'w-full items-start',
        className,
      )}
    >
      <div
        className={cn(
          'group/tabbar relative flex min-w-0 items-center',
          isInline ? 'w-fit max-w-full' : 'w-full',
        )}
      >
        {canScrollLeft && (
          <div className="pointer-events-none absolute left-0 top-0 bottom-0 z-10 flex items-center bg-gradient-to-r from-background via-background/90 to-transparent pr-3 opacity-0 transition-opacity duration-200 group-hover/tabbar:pointer-events-auto group-hover/tabbar:opacity-100 group-focus-within/tabbar:pointer-events-auto group-focus-within/tabbar:opacity-100">
            <button
              type="button"
              onClick={() => handleScroll('left')}
              disabled={disabled}
              aria-label="Scroll tabs left"
              className="flex size-7 items-center justify-center rounded-full border border-border/60 bg-background/95 text-foreground/80 shadow-xs backdrop-blur-xs transition-all hover:bg-accent hover:text-foreground active:scale-95 disabled:pointer-events-none disabled:opacity-50"
            >
              <ChevronLeft className="size-4" strokeWidth={2} />
            </button>
          </div>
        )}

        <div
          ref={scrollContainerRef}
          onScroll={updateScrollState}
          role="tablist"
          className={cn(
            'flex min-w-0 items-start overflow-x-auto overflow-y-clip scroll-smooth [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
            isInline ? 'w-fit max-w-full' : 'w-full scroll-px-4 snap-x snap-proximity',
            tabsClassName,
          )}
        >
          {tabs.map((tab) => {
            const isActive = activeHref === tab.href;

            return (
              <button
                key={tab.href}
                type="button"
                role="tab"
                aria-selected={isActive}
                disabled={disabled}
                onClick={() => onTabClick(tab.href)}
                className={cn(
                  'relative flex shrink-0 cursor-pointer snap-start flex-col items-stretch overflow-visible disabled:cursor-not-allowed disabled:opacity-50',
                  !isInline && 'min-h-[44px]',
                )}
              >
                <span className="flex items-start px-4 py-2.5">
                  <span
                    className={cn(
                      'whitespace-nowrap text-[14px] leading-normal',
                      isActive
                        ? 'font-semibold text-black dark:text-foreground'
                        : 'font-medium text-[#6d6d6d] hover:text-black dark:text-muted-foreground dark:hover:text-foreground',
                    )}
                  >
                    {tab.label}
                  </span>
                </span>
                <span
                  aria-hidden="true"
                  className={cn(
                    'absolute inset-x-0 bottom-0 z-[1] h-[2px]',
                    isActive ? 'bg-black dark:bg-foreground' : 'bg-transparent',
                  )}
                />
              </button>
            );
          })}
        </div>

        {canScrollRight && (
          <div className="pointer-events-none absolute right-0 top-0 bottom-0 z-10 flex items-center justify-end bg-gradient-to-l from-background via-background/90 to-transparent pl-3 opacity-0 transition-opacity duration-200 group-hover/tabbar:pointer-events-auto group-hover/tabbar:opacity-100 group-focus-within/tabbar:pointer-events-auto group-focus-within/tabbar:opacity-100">
            <button
              type="button"
              onClick={() => handleScroll('right')}
              disabled={disabled}
              aria-label="Scroll tabs right"
              className="flex size-7 items-center justify-center rounded-full border border-border/60 bg-background/95 text-foreground/80 shadow-xs backdrop-blur-xs transition-all hover:bg-accent hover:text-foreground active:scale-95 disabled:pointer-events-none disabled:opacity-50"
            >
              <ChevronRight className="size-4" strokeWidth={2} />
            </button>
          </div>
        )}
      </div>

      {showDivider ? (
        <div aria-hidden="true" className="h-px w-full shrink-0 bg-[#d1d1d1] dark:bg-border" />
      ) : null}
    </div>
  );
}
